import { useSyncExternalStore } from "react";
import { maybeDelayDevNetwork } from "@/lib/devNetworkDelay";

type NetworkActivitySnapshot = {
  pendingCount: number;
  visible: boolean;
  /**
   * When the current RUN began, as a performance.now() timestamp — the first request of the
   * burst, held steady across every request and every gap inside it. The loader stages its
   * phases against this. Null only when there is no run at all (grace window fully closed).
   *
   * A "run" is one continuous piece of user-facing work: it opens on the first tracked request
   * and stays open through the {@link HIDE_GRACE_MS} seams between sequential requests, so the
   * phase clock never rewinds mid-flow. It is NOT reset by nested activities.
   */
  startedAt: number | null;
  /**
   * Increments once per run. The loader keys its phase sequence off this: while it is
   * unchanged the sequence keeps advancing (even across a gap where `visible` briefly drops);
   * when it changes a genuinely new run has started and the sequence restarts.
   */
  runId: number;
  /**
   * What the pending work is about, for the loader's second line. The loader's animation stays
   * generic and the *copy* carries the context — "Syncing your calendars" tells the user
   * something, "Working on it" does not. Undefined falls back to the neutral caption.
   */
  label?: string;
};

/** Contextual captions for the significant loader. Kept here so the wording is defined once. */
export type ActivityLabel = string;

export type LoadingType = "user-blocking" | "background";
export type LoaderMode = "default" | "immediate";

type TrackedRequestOptions = RequestInit & {
  loaderMode?: LoaderMode;
  loadingType?: LoadingType;
  skipGlobalLoader?: boolean;
};

/**
 * Once a run has started, it stays open through gaps of up to this long between tracked
 * requests before closing. A multi-step interaction — auth hydrate then the dashboard's own
 * fetches; a few sequential POSTs; an upgrade that previews then changes plan then polls —
 * drops pendingCount to 0 in the seams. Without a rolling grace the run closes and the loader's
 * phase sequence restarts from "Starting…" in every seam. Anything genuinely finished is gone
 * within this window, so it does not add perceptible latency.
 *
 * (The loader still paints nothing for the first 450ms of a run — see useLoaderPhase — so a run
 * that opens and closes inside that window shows no UI at all.)
 */
const HIDE_GRACE_MS = 500;

let pendingCount = 0;
// Labels are held in a stack: nested activities each push one, and the most recent wins, so a
// specific inner operation is described rather than the broad wrapper around it.
const labelStack: { id: number; label: string }[] = [];
let labelSeq = 0;
// The current run: opened by the first request of a burst, held across the gaps between
// requests, and only closed once HIDE_GRACE_MS has elapsed with nothing pending. runStartedAt
// is the loader's phase-clock anchor; it does NOT move when nested activities begin.
let runStartedAt: number | null = null;
let runId = 0;
let runCloseTimer: number | null = null;
// Start times of everything in flight, oldest first — used only to anchor a brand-new run to
// the real start of its first request rather than to "now".
const activityStarts: { id: number; at: number }[] = [];
let snapshot: NetworkActivitySnapshot = {
  pendingCount: 0,
  visible: false,
  startedAt: null,
  runId: 0,
};

function currentLabel() {
  return labelStack.length ? labelStack[labelStack.length - 1].label : undefined;
}
const transitionActivities = new Map<string, () => void>();

const listeners = new Set<() => void>();

function emit() {
  snapshot = {
    pendingCount,
    // Kept for backward compatibility of the snapshot shape; "there is an open run" is the
    // single lifecycle signal now, and the loader derives everything from runId + startedAt.
    visible: runStartedAt !== null,
    startedAt: runStartedAt,
    runId,
    label: currentLabel(),
  };
  listeners.forEach((listener) => listener());
}

/** Open a run (or keep the current one open) when a request starts. */
function openOrKeepRun() {
  if (runCloseTimer !== null) {
    // A request arrived inside the grace window — the current run continues, uninterrupted.
    clearTimeout(runCloseTimer);
    runCloseTimer = null;
  }
  if (runStartedAt === null) {
    // Fresh run: anchor it to the real start of its first request, and bump runId so the
    // loader restarts its phase sequence.
    runStartedAt = activityStarts.length
      ? activityStarts[0].at
      : typeof performance !== "undefined" ? performance.now() : Date.now();
    runId += 1;
  }
}

/** Schedule the run to close once nothing has been pending for the grace period. */
function scheduleRunClose() {
  if (pendingCount > 0 || runStartedAt === null) return;
  if (runCloseTimer !== null) return;
  runCloseTimer = setTimeout(() => {
    runCloseTimer = null;
    if (pendingCount === 0) {
      runStartedAt = null;
      emit();
    }
  }, HIDE_GRACE_MS) as unknown as number;
}

function beginActivity(_loaderMode: LoaderMode, label?: string) {
  pendingCount += 1;
  const id = (labelSeq += 1);
  const started = { id, at: typeof performance !== "undefined" ? performance.now() : Date.now() };
  activityStarts.push(started);
  const entry = label ? { id, label } : null;
  if (entry) labelStack.push(entry);
  openOrKeepRun();
  emit();

  let finished = false;
  return () => {
    if (finished) return;
    finished = true;
    pendingCount = Math.max(0, pendingCount - 1);
    const startIndex = activityStarts.findIndex((item) => item.id === started.id);
    if (startIndex !== -1) activityStarts.splice(startIndex, 1);
    if (entry) {
      const index = labelStack.findIndex((item) => item.id === entry.id);
      if (index !== -1) labelStack.splice(index, 1);
    }
    emit();
    scheduleRunClose();
  };
}

export function beginGlobalActivity(loaderMode: LoaderMode = "default", label?: string) {
  return beginActivity(loaderMode, label);
}

export function startTransitionActivity(key: string, loaderMode: LoaderMode = "immediate", label?: string) {
  transitionActivities.get(key)?.();
  const end = beginActivity(loaderMode, label);
  transitionActivities.set(key, () => {
    transitionActivities.delete(key);
    end();
  });
}

export function endTransitionActivity(key: string) {
  transitionActivities.get(key)?.();
}

// Wraps a multi-step async workflow in a single loader transaction so the
// global loader stays visible for the entire duration, regardless of gaps
// between individual tracked requests (e.g. a background GET between two POSTs).
export async function withGlobalActivity<T>(fn: () => Promise<T>, loaderMode: LoaderMode = "immediate", label?: string): Promise<T> {
  const end = beginActivity(loaderMode, label);
  try {
    return await fn();
  } finally {
    end();
  }
}

export function getNetworkActivitySnapshot(): NetworkActivitySnapshot {
  return snapshot;
}

export function subscribeToNetworkActivity(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useNetworkActivity() {
  return useSyncExternalStore(subscribeToNetworkActivity, getNetworkActivitySnapshot, getNetworkActivitySnapshot);
}

export async function trackedFetch(input: RequestInfo | URL, init: TrackedRequestOptions = {}) {
  const {
    skipGlobalLoader = false,
    loaderMode = "default",
    loadingType = "background",
    ...requestInit
  } = init;
  const endTracking = !skipGlobalLoader && loadingType === "user-blocking"
    ? beginActivity(loaderMode)
    : null;
  try {
    await maybeDelayDevNetwork();
    return await fetch(input, requestInit);
  } finally {
    endTracking?.();
  }
}

export function waitForNextPaint() {
  if (typeof window === "undefined") return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}
